// === IMPORTS ===
import React, { useState, useRef, useEffect } from 'react';
import { OfficeLocation } from '../types';
import { Plus, ChevronDown, Check, Building2, Info } from 'lucide-react';
import './LocationSelectorBar.css';

interface LocationSelectorBarProps {
  locations: OfficeLocation[];
  activeLocationId: string;
  onSelectLocation: (locationId: string) => void;
  onOpenDetailsDrawer?: (locationId: string) => void;
  onOpenCreateDrawer?: () => void;
  onOpenManagerDrawer?: () => void;
}

// === COMPONENTE: BARRA SELECTORA DE PISO/UBICACIÓN (FLOTANTE SOBRE EL CANVAS) ===
export const LocationSelectorBar: React.FC<LocationSelectorBarProps> = ({
  locations,
  activeLocationId,
  onSelectLocation,
  onOpenDetailsDrawer,
  onOpenCreateDrawer,
  onOpenManagerDrawer,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeLocation = locations.find((l) => l.id === activeLocationId) || locations[0];

  const getPlacedAgentsCount = (loc?: OfficeLocation) => {
    if (!loc || !Array.isArray(loc.agents)) return 0;
    return loc.agents.filter(
      (ag) => ag.isPlaced === true || (ag.isPlaced !== false && ag.currentCol >= 0 && ag.currentRow >= 0)
    ).length;
  };

  // === CERRAR DROPDOWN CON CLIC AFUERA O TECLA ESCAPE ===
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleAddClick = () => {
    setIsOpen(false);
    if (onOpenCreateDrawer) {
      onOpenCreateDrawer();
    } else if (onOpenManagerDrawer) {
      onOpenManagerDrawer();
    }
  };

  const handleSelect = (locId: string) => {
    onSelectLocation(locId);
    setIsOpen(false);
    if (onOpenDetailsDrawer) {
      onOpenDetailsDrawer(locId);
    } else if (onOpenManagerDrawer) {
      onOpenManagerDrawer();
    }
  };

  const activePlacedAgentCount = getPlacedAgentsCount(activeLocation);

  // === RENDER ===
  return (
    <div className="twin-locbar" ref={dropdownRef} id="location-selector-bar">
      <div className="twin-locbar-trigger-wrap">
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className={`twin-locbar-trigger ${isOpen ? 'twin-locbar-trigger--open' : ''}`}
          title={`Selected floor: ${activeLocation?.name || 'Office'}. Click to view all floors or edit.`}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <div className="twin-locbar-trigger-left">
            <span className="twin-locbar-trigger-icon">{activeLocation?.icon || '🏢'}</span>
            <span className="twin-locbar-trigger-name">{activeLocation?.name || 'Floor'}</span>
          </div>

          <div className="twin-locbar-trigger-right">
            <span className="twin-locbar-count-badge" title={`${activePlacedAgentCount} agents assigned to this floor`}>
              {activePlacedAgentCount}
            </span>
            <ChevronDown className={`twin-icon-xs twin-locbar-chevron ${isOpen ? 'twin-locbar-chevron--open' : ''}`} />
          </div>
        </button>

        {isOpen && (
          <div className="twin-locbar-dropdown">
            <div className="twin-locbar-dropdown-header">
              <span className="twin-locbar-dropdown-title">
                <Building2 className="twin-icon-xxs twin-color-theme-accent" />
                <span>Building Floors ({locations.length})</span>
              </span>
              <span className="twin-locbar-dropdown-hint">Click to open &amp; view info</span>
            </div>

            <div className="twin-locbar-list" role="listbox">
              {locations.map((loc) => {
                const isSelected = loc.id === activeLocationId;
                const placedCount = getPlacedAgentsCount(loc);

                return (
                  <button
                    key={loc.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(loc.id)}
                    className={`twin-locbar-item ${isSelected ? 'twin-locbar-item--selected' : ''}`}
                  >
                    <div className="twin-locbar-item-left">
                      <div className="twin-locbar-item-icon">{loc.icon || '🏢'}</div>
                      <div className="twin-locbar-item-text">
                        <div className="twin-locbar-item-name-row">
                          <p className={`twin-locbar-item-name ${isSelected ? 'twin-locbar-item-name--selected' : ''}`}>{loc.name}</p>
                          {isSelected && <span className="twin-locbar-active-badge">Active</span>}
                        </div>
                        <p className="twin-locbar-item-sub">
                          {loc.gridWidth}x{loc.gridHeight} ({((loc.gridWidth * (loc.tileSizeMeters ?? 1.0)) * (loc.gridHeight * (loc.tileSizeMeters ?? 1.0))).toFixed(0)}m²) • {loc.rooms?.length || 0} zones
                        </p>
                      </div>
                    </div>

                    <div className="twin-locbar-item-right">
                      <span className={`twin-locbar-agent-count ${isSelected ? 'twin-locbar-agent-count--selected' : ''}`}>
                        {placedCount} {placedCount === 1 ? 'agent' : 'agents'}
                      </span>
                      {isSelected ? (
                        <Check className="twin-icon-xs twin-color-theme-accent" />
                      ) : (
                        <Info className="twin-icon-xs twin-locbar-info-icon" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="twin-locbar-footer">
              <button type="button" onClick={handleAddClick} className="twin-locbar-add-btn">
                <Plus className="twin-icon-xs" />
                <span>Create new floor...</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};